import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Brain, Loader2 } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { apiClient } from '@/lib/api';

interface MemoryEntry {
  id: string;
  assistantId: string;
  memoryType: 'fact' | 'preference' | 'instruction' | 'context';
  memoryKey: string;
  memoryValue: string;
  confidence: number;
  createdAt: string;
  updatedAt: string;
}

const TYPE_TONES: Record<MemoryEntry['memoryType'], string> = {
  fact:        'bg-blue-100 text-blue-700',
  preference:  'bg-violet-100 text-violet-700',
  instruction: 'bg-amber-100 text-amber-700',
  context:     'bg-muted text-foreground',
};

interface Props {
  assistantId: string;
}

export const MemoryPanel: React.FC<Props> = ({ assistantId }) => {
  const [items, setItems] = useState<MemoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draftKey, setDraftKey] = useState('');
  const [draftValue, setDraftValue] = useState('');
  const [draftType, setDraftType] = useState<MemoryEntry['memoryType']>('fact');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<MemoryEntry[]>(
        `/personal-assistant/workspace/assistants/${assistantId}/memory`,
      );
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('[MemoryPanel] load failed', err);
    } finally {
      setLoading(false);
    }
  }, [assistantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitNew = async () => {
    if (!draftKey.trim() || !draftValue.trim()) return;
    try {
      await apiClient.post(`/personal-assistant/workspace/assistants/${assistantId}/memory`, {
        memoryType: draftType,
        memoryKey: draftKey.trim(),
        memoryValue: draftValue.trim(),
      });
      setDraftKey('');
      setDraftValue('');
      setAdding(false);
      refresh();
    } catch (err) {
      console.warn('[MemoryPanel] upsert failed', err);
    }
  };

  const removeOne = async (id: string) => {
    try {
      await apiClient.delete(`/personal-assistant/workspace/assistants/${assistantId}/memory/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.warn('[MemoryPanel] delete failed', err);
    }
  };

  const handleConfirmDelete = (id: string) => {
    if (confirmDeleteId === id) {
      setConfirmDeleteId(null);
      void removeOne(id);
    } else {
      setConfirmDeleteId(id);
      window.setTimeout(() => {
        setConfirmDeleteId(prev => (prev === id ? null : prev));
      }, 4000);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-2 py-2">
        <span className="text-[11px] font-semibold uppercase text-muted-foreground inline-flex items-center gap-1">
          <Brain className="h-3 w-3" /> Memoria persistente
        </span>
        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => setAdding(s => !s)}>
          <Plus className="h-3 w-3 mr-0.5" /> {adding ? 'Annulla' : 'Aggiungi'}
        </Button>
      </div>

      {adding && (
        <div className="px-2 pb-2 space-y-1.5 border-b border-border">
          <select
            value={draftType}
            onChange={(e) => setDraftType(e.target.value as MemoryEntry['memoryType'])}
            className="w-full text-xs border border-border rounded px-2 py-1"
          >
            <option value="fact">Fatto</option>
            <option value="preference">Preferenza</option>
            <option value="instruction">Istruzione</option>
            <option value="context">Contesto</option>
          </select>
          <input
            type="text"
            value={draftKey}
            onChange={(e) => setDraftKey(e.target.value)}
            placeholder="Chiave (es: lingua_preferita)"
            className="w-full text-xs border border-border rounded px-2 py-1"
          />
          <textarea
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            placeholder="Valore"
            rows={2}
            className="w-full text-xs border border-border rounded px-2 py-1 resize-none"
          />
          <Button size="sm" className="w-full h-7 text-xs" onClick={submitNew} disabled={!draftKey.trim() || !draftValue.trim()}>
            Salva
          </Button>
        </div>
      )}

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground px-2 py-4 text-center">
            Nessuna memoria salvata
          </div>
        ) : (
          <ul className="px-2 pb-2 space-y-1">
            {items.map(m => (
              <li key={m.id} className="group rounded border border-border bg-card p-2">
                <div className="flex items-center gap-1 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${TYPE_TONES[m.memoryType]}`}>
                    {m.memoryType}
                  </span>
                  <span className="text-[11px] font-medium text-foreground truncate flex-1" title={m.memoryKey}>{m.memoryKey}</span>
                  <button
                    type="button"
                    onClick={() => handleConfirmDelete(m.id)}
                    aria-label={confirmDeleteId === m.id ? 'Conferma eliminazione' : 'Elimina memoria'}
                    className={`shrink-0 h-5 min-w-[20px] px-1 rounded border text-[10px] inline-flex items-center justify-center gap-1 transition ${
                      confirmDeleteId === m.id
                        ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                        : 'bg-card border-border text-muted-foreground hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                    }`}
                    title={confirmDeleteId === m.id ? 'Conferma eliminazione' : 'Elimina'}
                  >
                    <Trash2 className="h-3 w-3" />
                    {confirmDeleteId === m.id && <span>Conferma</span>}
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground break-words">{m.memoryValue}</div>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
};
